import { completedPeriod, loadStatistics, type StatisticsSummary, type StatisticsPeriod } from "./statistics";
import type { HomeDashboardRepository } from "./home-dashboard";
import type { MealRepository } from "./meal-repository";
import { localISODate } from "./water-repository";

export interface StatisticsReport { id: string; savedAt: string; summary: StatisticsSummary }
export const reportId = (period: StatisticsPeriod) => `${period.kind}:${period.start}:${period.end}`;
function openReports(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open("yumnote-statistics", 1);
    request.onupgradeneeded = () => request.result.createObjectStore("reports", { keyPath: "id" });
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}
export async function readReports(): Promise<StatisticsReport[]> {
  const db = await openReports();
  try { return await new Promise<StatisticsReport[]>((resolve, reject) => {
    const request = db.transaction("reports").objectStore("reports").getAll();
    request.onsuccess = () => resolve((request.result as StatisticsReport[]).sort((a, b) => b.summary.period.end.localeCompare(a.summary.period.end)));
    request.onerror = () => reject(request.error);
  }); } finally { db.close(); }
}
export async function saveReport(summary: StatisticsSummary): Promise<StatisticsReport> {
  const report = { id: reportId(summary.period), savedAt: new Date().toISOString(), summary };
  const db = await openReports();
  try { await new Promise<void>((resolve, reject) => {
    const tx = db.transaction("reports", "readwrite");
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error ?? new Error("Не удалось сохранить отчёт"));
    tx.objectStore("reports").put(report);
  }); } finally { db.close(); }
  return report;
}
let generating: Promise<void> | null = null;
export function generateReports(dashboards: HomeDashboardRepository, meals: MealRepository): Promise<void> {
  if (generating) return generating;
  generating = (async () => {
    const existing = new Set((await readReports()).map(report => report.id));
    for (const kind of ["week", "month", "quarter", "year"] as const) {
      const period = completedPeriod(kind, localISODate());
      if (existing.has(reportId(period))) continue;
      const summary = await loadStatistics(period, dashboards, meals);
      if (summary.loggedDays || summary.activityDays) await saveReport(summary);
    }
  })().finally(() => { generating = null; });
  return generating;
}
